/**
 * client_mode_controller.js — Existing/new client toggle for create forms (Stimulus).
 *
 * The markup it drives lives in components/forms/_client_site_fields.html,
 * which the job form and the quote form share; both declare
 * data-controller="client-mode" with the two endpoints they post to.
 *
 * Behaviour:
 *   Existing -> the picker block shows, the client select is required, and the
 *               new-client block is hidden with its inputs not required.
 *   New      -> the reverse: phone and address are required, and client-type
 *               decides which of the name fields a new client actually needs.
 *
 * The required flags are the whole point. They are what form-guard reads to say
 * what the form still needs, and a required input inside a display:none
 * container is one a browser refuses to submit AND refuses to focus, so a form
 * left flagged that way dies silently. That is why apply() runs on connect and
 * after a form reset as well as on change.
 *
 * The client select is flagged with data-required rather than `required`: its
 * native element is hidden by Choices.js, which a browser cannot focus to
 * report on. Address is required for every new client, business or individual,
 * because the quote or job inherits it as its site (client_intake_form.py holds
 * the server's copy of the same rule).
 *
 * Which name fields a new client needs is NOT decided here — client-type owns
 * that. This controller announces `client-mode:applied` on the new-client block
 * once its visibility is settled, and client-type re-derives its flags from it.
 * Two controllers writing the same `required` would fight on every tap.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ['radio', 'existingFields', 'newFields', 'clientSelect'];
    static values = { existingUrl: String, newUrl: String };

    connect() {
        // `reset` fires before the browser restores the radios, so apply()
        // waits a task to read the restored mode (a cancelled create).
        this._onReset = () => setTimeout(() => this.apply());
        this.element.addEventListener('reset', this._onReset);
        this.apply();
    }

    disconnect() {
        this.element.removeEventListener('reset', this._onReset);
    }

    apply() {
        const selected = this.radioTargets.find((radio) => radio.checked);
        const isNew = selected ? selected.value === 'new' : false;

        // Which endpoint the form posts to IS the mode, so no server-side
        // reader has to agree with this template about what the radio means.
        if (this.hasNewUrlValue && this.hasExistingUrlValue) {
            this.element.action = isNew ? this.newUrlValue : this.existingUrlValue;
        }

        if (this.hasExistingFieldsTarget) {
            this.existingFieldsTarget.style.display = isNew ? 'none' : '';
        }
        if (this.hasNewFieldsTarget) {
            this.newFieldsTarget.style.display = isNew ? 'block' : 'none';
        }
        if (this.hasClientSelectTarget) {
            this.clientSelectTarget.dataset.required = String(!isNew);
        }
        this._requireNewClientFields(isNew);
    }

    _requireNewClientFields(isNew) {
        if (!this.hasNewFieldsTarget) return;
        // Phone and address are what a new client always needs, whoever they
        // are: a name may be a business name or a person's, which client-type
        // sorts out, but nothing reaches a client without a number to ring or
        // a place to go.
        ['phone', 'address_input'].forEach((name) => {
            const input = this.newFieldsTarget.querySelector(`input[name="${name}"]`);
            if (input) input.required = isNew;
        });
        if (!isNew) {
            this.newFieldsTarget
                .querySelectorAll('input[required]')
                .forEach((input) => { input.required = false; });
        }
        this.dispatch('applied', { target: this.newFieldsTarget });
    }
}
